import { getBuiltInManifest } from '@/data/manifests';
import { getAllBuiltinOperations } from '@/types/operations';
import { isManagedApiConnector } from '@/utils/connections';
import type { SelectedNode } from '@/stores/nodeEditorStore';
import { useNodeOperationInfo } from './useNodeOperationInfo';
import { useNodeConnectorInfo } from './useNodeConnectorInfo';
import { useOperationManifest } from './useOperationManifest';

const builtInOperations = getAllBuiltinOperations();

export function useNodeAboutMetadata(node: SelectedNode, parameterValues: Record<string, unknown>) {
  const { connectorInfo } = useNodeConnectorInfo(node);
  const { operationInfo, serviceProviderConfig } = useNodeOperationInfo({ nodeData: node.nodeData, parameterValues });
  const result = useOperationManifest(operationInfo);
  const kind = node.nodeData && 'kind' in node.nodeData && typeof node.nodeData.kind === 'string'
    ? node.nodeData.kind : undefined;
  const builtIn = getBuiltInManifest(node.nodeData?.type ?? '', kind);
  const properties = builtIn?.properties;
  const catalogOperation = builtInOperations.find((operation) =>
    operation.type === node.nodeData?.type && !operation.isConnector
    && (node.type === 'trigger' ? operation.category === 'trigger' : operation.category !== 'trigger')
    && (kind ? operation.kind === kind : !operation.kind));
  const metadata = result.data?.operationMetadata;
  const connectorDisplayName = connectorInfo?.connectorType === 'managedApi'
    || connectorInfo?.connectorType === 'serviceProvider' ? connectorInfo.displayName.trim() : undefined;
  const connectorId = operationInfo?.connectorId ?? '';
  const connectorType = isManagedApiConnector(connectorId)
    ? 'API connection'
    : serviceProviderConfig?.serviceProviderId
      ? 'Built-in (service provider)'
      : builtIn || connectorId.startsWith('connectionProviders/') || connectorId.startsWith('/connectionProviders/')
        ? 'Built-in'
        : undefined;
  return {
    connectorType,
    connectorName: connectorDisplayName || metadata?.connectorName?.trim()
      || properties?.connector?.properties?.displayName?.trim() || properties?.connector?.displayName?.trim(),
    operationName: (builtIn ? properties?.summary ?? catalogOperation?.name : metadata?.name)?.trim(),
    description: (builtIn ? properties?.description : metadata?.description)?.trim(),
    iconUri: connectorInfo?.iconUri || properties?.iconUri || (metadata ? result.data?.properties.iconUri : undefined),
    brandColor: connectorInfo?.brandColor || properties?.brandColor || (metadata ? result.data?.properties.brandColor : undefined),
    isLoading: !builtIn && result.isLoading,
    error: !builtIn && result.metadataError,
    retry: result.refetchMetadata,
  };
}
